import React, { useCallback, useEffect, useState } from 'react';
import { useTable } from './use-synchronize';
import { map } from 'rxjs/operators';
import Table from '../../../../../../components/common/table/table';
import css from './tables.module.css';
import Button from './button';
import { FieldForSync, useFieldsForMapping } from './synchronize-context';
import { newFieldFromSystem } from './current-field-status-with-lad-table';
import Edit from './edit';
import { nanoid } from '@reduxjs/toolkit';

export interface FieldNotRegisteredInLad extends FieldForSync {
  logo: null;
}

export default function FieldsNotRegisteredInLadTable() {
  const { t, tp, farmId, season, fieldService, selectedFields } =
    useTable<FieldNotRegisteredInLad>();
  const [state, setState] = useState<FieldNotRegisteredInLad[]>([]);
  const {
    setCurrentLadStatusWithLadState,
    currentLadStatusWithLadState: {
      editable: currentLadStatusWithLadEditableState,
      state: currentLadStatusWithLadState,
    },
    setFieldsNotRegisteredInLadState,
    mappingState,
  } = useFieldsForMapping();

  const [checkedFields, setCheckedFields] = selectedFields;

  useEffect(() => {
    setFieldsNotRegisteredInLadState((prevState) => ({
      ...prevState,
      checked: checkedFields,
    }));
  }, [checkedFields, setFieldsNotRegisteredInLadState]);

  const request = useCallback(
    () =>
      fieldService.notRegisteredInLad(farmId, season).pipe(
        map((request) => {
          const idsOfFieldsToHide = new Set([
            ...currentLadStatusWithLadEditableState.map(({ id }) => id),
            ...currentLadStatusWithLadState.map(({ id }) => id),
            ...mappingState.state.map(({ id }) => id),
          ]);

          const data = request.data
            .map((field) => ({
              ...field,
              logo: null,
            }))
            .filter(({ id }) => !idsOfFieldsToHide.has(id))
            .filter(
              ({ mapped_fields_group, mapped_fields_ids }) =>
                !(mapped_fields_group && mapped_fields_ids)
            );

          setState(data);

          return {
            ...request,
            data,
          };
        })
      ),
    [
      fieldService,
      farmId,
      season,
      currentLadStatusWithLadEditableState,
      currentLadStatusWithLadState,
      mappingState.state,
    ]
  );

  const onSaveButtonClick = () => {
    const transformedFields = state
      .map((field) => ({
        ...field,
        active: checkedFields.some(({ id }) => id === field.id),
      }))
      .filter((field) => field.active);

    setCurrentLadStatusWithLadState((prevState) => ({
      ...prevState,
      editable: [
        ...prevState.editable,
        ...transformedFields.map((field) => ({
          ...field,
          key: nanoid(),
          editable: <Edit />,
          isCheckable: false,
          stored: newFieldFromSystem,
          first_local_field: false,
          last_local_field: false,
        })),
      ],
    }));

    setState([]);
    setCheckedFields([]);
  };

  const saveButton = (
    <Button
      text={t('save')}
      disabled={checkedFields.length === 0}
      onClick={onSaveButtonClick}
    />
  );

  const table = (
    <div className={`flex flex-col ${css['colored-head']}`}>
      <p className="px-4 py-4 pb-0">{tp('fields_not_registered_in_lad')}</p>
      <Table<FieldNotRegisteredInLad>
        request={request}
        noDataMessage={tp('no_fields')}
        keyAccessor="lad_number"
        paginate={false}
        selectedRowsState={selectedFields}
        columns={[
          {
            accessor: 'logo',
            header: '',
            headerClassName: css['logo'],
          },
          {
            accessor: 'lad_number',
            header: tp('lad_number'),
          },
          {
            accessor: 'name',
            header: tp('name'),
          },
          {
            accessor: 'block_number',
            header: tp('block_number'),
          },
          {
            accessor: 'area',
            header: tp('area'),
          },
          {
            accessor: 'crop_value',
            header: tp('crop'),
          },
        ]}
      />
      <div className="self-end p-4 pt-0">{saveButton}</div>
    </div>
  );

  return table;
}
